(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class FjCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.opener = null;
      this.closeTimer = null;
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleCartAdded = this.handleCartAdded.bind(this);
      this.checkOverflow = this.checkOverflow.bind(this);
    }

    connectedCallback() {
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('fj:cart:added', this.handleCartAdded);
      document.addEventListener('fj:cart:updated', this.checkOverflow);
      window.addEventListener('resize', this.checkOverflow);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('fj:cart:added', this.handleCartAdded);
      document.body.classList.remove('fj-scroll-lock');
      document.removeEventListener('fj:cart:updated', this.checkOverflow);
      window.removeEventListener('resize', this.checkOverflow);
      clearTimeout(this.closeTimer);
    }

    get isOpen() {
      return !this.hasAttribute('hidden');
    }

    /* Footer shadow only when the item list has more than fits. */
    checkOverflow() {
      requestAnimationFrame(() => {
        const list = this.querySelector('[data-fj-cart-drawer-scroll]');
        const overflowing = Boolean(list) && list.scrollHeight > list.clientHeight;
        this.classList.toggle('fj-cart-drawer--overflowing', overflowing);
      });
    }

    handleCartAdded() {
      const hasItems = this.querySelector('[data-fj-cart-line]');
      if (!hasItems) this.classList.add('fj-cart-drawer--pending');
      this.open();
      this.refresh();
    }

    /* The drawer markup was rendered with the cart as it was at page
       load, so pull a fresh copy of the section before showing it. */
    async refresh() {
      const inner = this.querySelector('fj-cart[data-fj-section-id]');
      if (!inner) return;

      const sectionId = inner.getAttribute('data-fj-section-id');
      this.classList.add('fj-cart-drawer--loading');

      try {
        const [sectionsResponse, cartResponse] = await Promise.all([
          fetch(`${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionId)}`),
          fetch(this.getAttribute('data-fj-cart-url'))
        ]);

        const sections = await sectionsResponse.json();

        const FjCart = customElements.get('fj-cart');
        if (FjCart) FjCart.renderSections(sections);

        const cart = await cartResponse.json();

        this.dispatchEvent(
          new CustomEvent('fj:cart:updated', {
            bubbles: true,
            detail: { itemCount: cart.item_count, cart }
          })
        );
      } catch (error) {
        /* Keep the last known contents rather than blanking the drawer. */
      } finally {
        this.classList.remove('fj-cart-drawer--loading', 'fj-cart-drawer--pending');
      }
    }

    handleDocumentClick(event) {
      const toggle = event.target.closest('[data-fj-cart-drawer-toggle]');

      if (toggle) {
        event.preventDefault();
        this.open(toggle);
        return;
      }

      if (this.contains(event.target) && event.target.closest('[data-fj-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      clearTimeout(this.closeTimer);
      this.opener = opener || null;
      this.removeAttribute('hidden');
      document.body.classList.add('fj-scroll-lock');

      requestAnimationFrame(() => {
        this.classList.add('fj-cart-drawer--open');
        const initialFocus = this.querySelector('[data-fj-cart-drawer-initial-focus]');
        if (initialFocus) initialFocus.focus();
        this.checkOverflow();
      });
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove('fj-cart-drawer--open');
      document.body.classList.remove('fj-scroll-lock');

      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }

      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.setAttribute('hidden', ''), 300);
    }
  }

  if (!customElements.get('fj-cart-drawer')) {
    customElements.define('fj-cart-drawer', FjCartDrawer);
  }
})();